/**
 * Centralized constants for reporting modals and components.
 * This file consolidates all scattered constants, option lists, form states,
 * and other configuration values used across reporting views.
 */

import type { ChartType, StatCardConfig } from "@hooks/use-reports/types";
import type { Theme } from "@mui/material";
import { alpha } from "@mui/material";
import { colors } from "@utils/colors";
import { TableCellInputType } from "@components/data-table/types";
// Financial / Stats
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalculateIcon from "@mui/icons-material/Calculate";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TimelineIcon from "@mui/icons-material/Timeline";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
// Assets / Hardware
import DevicesIcon from "@mui/icons-material/Devices";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import LaptopIcon from "@mui/icons-material/Laptop";
import TabletIcon from "@mui/icons-material/Tablet";
import PrintIcon from "@mui/icons-material/Print";
import StorageIcon from "@mui/icons-material/Storage";
import InventoryIcon from "@mui/icons-material/Inventory";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import WarehouseIcon from "@mui/icons-material/Warehouse";
// Tickets / Support
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import BugReportIcon from "@mui/icons-material/BugReport";
// Status / Workflow
import BuildIcon from "@mui/icons-material/Build";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import HandymanIcon from "@mui/icons-material/Handyman";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import PendingIcon from "@mui/icons-material/Pending";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import HelpIcon from "@mui/icons-material/Help";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
// Warranty / Dates
import GppGoodIcon from "@mui/icons-material/GppGood";
import GppBadIcon from "@mui/icons-material/GppBad";
import SecurityIcon from "@mui/icons-material/Security";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import HistoryIcon from "@mui/icons-material/History";
import UpdateIcon from "@mui/icons-material/Update";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TimerIcon from "@mui/icons-material/Timer";
// People / Assignment
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GroupIcon from "@mui/icons-material/Group";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
// Location / Logistics
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SchoolIcon from "@mui/icons-material/School";
// Other
import InfoIcon from "@mui/icons-material/Info";

// ============================================================================
// CHART TYPE OPTIONS
// ============================================================================

export const CHART_TYPE_OPTIONS: { value: ChartType; label: string; description: string }[] = [
  { value: "bar", label: "Bar Chart", description: "Compare values across categories" },
  { value: "pie", label: "Pie Chart", description: "Show proportions of a whole" },
  { value: "line", label: "Line Chart", description: "Show trends over a single dimension" },
  { value: "stacked-bar", label: "Stacked Bar Chart", description: "Compare parts of a whole across categories" },
  { value: "heatmap", label: "Heatmap", description: "Show density across two dimensions" },
  { value: "multi-line", label: "Multi-Line Chart", description: "Compare multiple series over a dimension" },
];

// ============================================================================
// EXCLUDED REPORT COLUMNS
// Columns excluded from groupBy and filter pickers in report configuration.
// ============================================================================

export const EXCLUDED_REPORT_COLUMNS = new Set(["id", "ticket_to_asset_to", "student_id", "first_name", "last_name"]);

// ============================================================================
// UI COLOURS
// ============================================================================

/** Background highlight used for user-generated items (reports, charts, stat cards). */
export const USER_GENERATED_HIGHLIGHT_BG = "#f0f7ff";

/** Theme-aware version of the highlight above — the light-mode value stays
 * exactly the same, but dark mode now gets a matching sky-tinted highlight
 * instead of the light-only hex value. */
export const getUserGeneratedHighlightBg = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? alpha(colors.sky, 0.16)
    : USER_GENERATED_HIGHLIGHT_BG;

// ============================================================================
// SORT OPTIONS
// ============================================================================

export const SORT_OPTIONS = [
  { value: "", label: "Default (alphabetical)" },
  { value: "alphabetical", label: "Alphabetical" },
  { value: "high-to-low", label: "High to Low" },
  { value: "low-to-high", label: "Low to High" },
];

// ============================================================================
// DATE AGGREGATION OPTIONS
// ============================================================================

export const DATE_AGGREGATION_OPTIONS = [
  { value: "", label: "None" },
  { value: "month", label: "By Month" },
  { value: "year", label: "By Year" },
];

// ============================================================================
// WIDTH OPTIONS
// ============================================================================

export const WIDTH_OPTIONS = [
  { value: "full", label: "Full width" },
  { value: "half", label: "Half width" },
];

// ============================================================================
// PERIOD OPTIONS (for stat cards)
// ============================================================================

export const PERIOD_OPTIONS: { value: StatCardConfig["period"]; label: string }[] = [
  { value: "current-month",  label: "Current Month" },
  { value: "last-month",     label: "Last Month" },
  { value: "last-30-days",   label: "Last 30 Days" },
  { value: "next-30-days",   label: "Next 30 Days" },
  { value: "next-month",     label: "Next Month" },
  { value: "current-year",   label: "Current Year" },
  { value: "last-year",      label: "Last Year" },
  { value: "last-365-days",  label: "Last 365 Days" },
  { value: "next-365-days",  label: "Next 365 Days" },
  { value: "next-year",      label: "Next Year" },
];

export const PERIOD_LABEL_MAP: Record<string, string> = Object.fromEntries(
  PERIOD_OPTIONS.map((o) => [o.value, o.label])
);

/**
 * Complete map of Material-UI icon names to icon components.
 * Cached here to avoid recreating on every render.
 */
export const ICON_MAP: Record<string, React.ComponentType<any>> = {
  // Financial / Stats
  AttachMoneyIcon,
  CalculateIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  TimelineIcon,
  PriceCheckIcon,
  // Assets / Hardware
  DevicesIcon,
  DevicesOtherIcon,
  LaptopIcon,
  TabletIcon,
  PrintIcon,
  StorageIcon,
  InventoryIcon,
  Inventory2Icon,
  WarehouseIcon,
  // Tickets / Support
  ConfirmationNumberIcon,
  AssignmentIcon,
  AssignmentTurnedInIcon,
  AssignmentLateIcon,
  SupportAgentIcon,
  HeadsetMicIcon,
  BugReportIcon,
  // Status / Workflow
  BuildIcon,
  BuildCircleIcon,
  HandymanIcon,
  CheckCircleIcon,
  VerifiedIcon,
  PendingIcon,
  PendingActionsIcon,
  BlockIcon,
  WarningAmberIcon,
  WarningIcon,
  ErrorIcon,
  HelpIcon,
  HelpOutlineIcon,
  // Warranty / Dates
  GppGoodIcon,
  GppBadIcon,
  SecurityIcon,
  CalendarMonthIcon,
  EventIcon,
  HistoryIcon,
  UpdateIcon,
  ScheduleIcon,
  TimerIcon,
  // People / Assignment
  PersonIcon,
  PersonOffIcon,
  GroupIcon,
  ManageAccountsIcon,
  // Location / Logistics
  LocationOnIcon,
  LocalShippingIcon,
  SchoolIcon,
  // Other
  InfoIcon,
};

/**
 * Icon configuration with display names and components.
 * Used by AddStatCardModal for icon selection UI.
 */
export const AVAILABLE_ICONS = [
  // Financial / Stats
  { id: "AttachMoneyIcon", label: "Money", component: AttachMoneyIcon },
  { id: "CalculateIcon", label: "Calculate", component: CalculateIcon },
  { id: "TrendingUpIcon", label: "Trending Up", component: TrendingUpIcon },
  { id: "TrendingDownIcon", label: "Trending Down", component: TrendingDownIcon },
  { id: "TimelineIcon", label: "Timeline", component: TimelineIcon },
  { id: "PriceCheckIcon", label: "Price Check", component: PriceCheckIcon },
  // Assets / Hardware
  { id: "DevicesIcon", label: "Devices", component: DevicesIcon },
  { id: "DevicesOtherIcon", label: "Devices Other", component: DevicesOtherIcon },
  { id: "LaptopIcon", label: "Laptop", component: LaptopIcon },
  { id: "TabletIcon", label: "Tablet", component: TabletIcon },
  { id: "PrintIcon", label: "Printer", component: PrintIcon },
  { id: "StorageIcon", label: "Storage", component: StorageIcon },
  { id: "InventoryIcon", label: "Inventory", component: InventoryIcon },
  { id: "Inventory2Icon", label: "Inventory 2", component: Inventory2Icon },
  { id: "WarehouseIcon", label: "Warehouse", component: WarehouseIcon },
  // Tickets / Support
  { id: "ConfirmationNumberIcon", label: "Ticket", component: ConfirmationNumberIcon },
  { id: "AssignmentIcon", label: "Assignment", component: AssignmentIcon },
  { id: "AssignmentTurnedInIcon", label: "Assignment Done", component: AssignmentTurnedInIcon },
  { id: "AssignmentLateIcon", label: "Assignment Late", component: AssignmentLateIcon },
  { id: "SupportAgentIcon", label: "Support Agent", component: SupportAgentIcon },
  { id: "HeadsetMicIcon", label: "Headset / Support", component: HeadsetMicIcon },
  { id: "BugReportIcon", label: "Bug Report", component: BugReportIcon },
  // Status / Workflow
  { id: "BuildIcon", label: "Build / Wrench", component: BuildIcon },
  { id: "BuildCircleIcon", label: "Build Circle", component: BuildCircleIcon },
  { id: "HandymanIcon", label: "Handyman", component: HandymanIcon },
  { id: "CheckCircleIcon", label: "Check Circle", component: CheckCircleIcon },
  { id: "VerifiedIcon", label: "Verified", component: VerifiedIcon },
  { id: "PendingIcon", label: "Pending", component: PendingIcon },
  { id: "PendingActionsIcon", label: "Pending Actions", component: PendingActionsIcon },
  { id: "BlockIcon", label: "Blocked", component: BlockIcon },
  { id: "WarningAmberIcon", label: "Warning (Amber)", component: WarningAmberIcon },
  { id: "WarningIcon", label: "Warning", component: WarningIcon },
  { id: "ErrorIcon", label: "Error", component: ErrorIcon },
  { id: "HelpIcon", label: "Help", component: HelpIcon },
  { id: "HelpOutlineIcon", label: "Help Outline", component: HelpOutlineIcon },
  // Warranty / Dates
  { id: "GppGoodIcon", label: "Shield / Warranty", component: GppGoodIcon },
  { id: "GppBadIcon", label: "Shield Expired", component: GppBadIcon },
  { id: "SecurityIcon", label: "Security", component: SecurityIcon },
  { id: "CalendarMonthIcon", label: "Calendar", component: CalendarMonthIcon },
  { id: "EventIcon", label: "Event", component: EventIcon },
  { id: "HistoryIcon", label: "History", component: HistoryIcon },
  { id: "UpdateIcon", label: "Update", component: UpdateIcon },
  { id: "ScheduleIcon", label: "Schedule", component: ScheduleIcon },
  { id: "TimerIcon", label: "Timer", component: TimerIcon },
  // People / Assignment
  { id: "PersonIcon", label: "Person", component: PersonIcon },
  { id: "PersonOffIcon", label: "Person Unassigned", component: PersonOffIcon },
  { id: "GroupIcon", label: "Group", component: GroupIcon },
  { id: "ManageAccountsIcon", label: "Manage Accounts", component: ManageAccountsIcon },
  // Location / Logistics
  { id: "LocationOnIcon", label: "Location Pin", component: LocationOnIcon },
  { id: "LocalShippingIcon", label: "Shipping", component: LocalShippingIcon },
  { id: "SchoolIcon", label: "School", component: SchoolIcon },
  // Other
  { id: "InfoIcon", label: "Info", component: InfoIcon },
];

// ============================================================================
// EMPTY FORM STATES
// ============================================================================

export interface ChartFormState {
  title: string;
  type: ChartType;
  width: "full" | "half";
  dimensions: (string | null)[];
  dateAggregation: "" | "month" | "year";
  sort: string;
  tooltip: string;
  filterColumn: string;
  filterValues: string[];
  useFilter: boolean;
}

export const EMPTY_CHART_FORM_STATE: ChartFormState = {
  title: "",
  type: "bar",
  width: "half",
  dimensions: [null],
  dateAggregation: "",
  sort: "",
  tooltip: "",
  filterColumn: "",
  filterValues: [],
  useFilter: false,
};

export interface StatCardFormState {
  title: string;
  aggregation: "sum" | "average" | "count" | "total-items";
  dimensions: (string | null)[];
  icon: string;
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error";
  prefix: string;
  suffix: string;
  tooltip: string;
  /** AND-combined column filters applied before aggregation. */
  filterConditions: Array<{ column: string; values: string[] }>;
  period: StatCardConfig["period"] | "";
  periodColumn: string;
  usePeriod: boolean;
}

export const EMPTY_STAT_FORM_STATE: StatCardFormState = {
  title: "",
  aggregation: "sum",
  dimensions: [null],
  icon: "InfoIcon",
  color: "primary",
  prefix: "",
  suffix: "",
  tooltip: "",
  filterConditions: [],
  period: "",
  periodColumn: "",
  usePeriod: false,
};

// ============================================================================
// FIELD TYPE CONFIGURATION
// ============================================================================

/**
 * Field types suitable for grouping/aggregation in stat cards
 */
export const GROUPABLE_INPUT_TYPES = new Set([
  TableCellInputType.Text,
  TableCellInputType.Select,
  TableCellInputType.Date,
  TableCellInputType.DateTime,
  TableCellInputType.Boolean,
]);

/**
 * Field types NOT suitable for grouping (too verbose)
 */
export const NON_GROUPABLE_INPUT_TYPES = new Set([
  TableCellInputType.MultiLine,
  TableCellInputType.FileUpload,
  TableCellInputType.UserInput,
  TableCellInputType.Form,
  TableCellInputType.SystemProvided,
]);

/**
 * Display labels for field types
 */
export const FIELD_TYPE_LABELS: Record<TableCellInputType, string> = {
  [TableCellInputType.Text]: "Text",
  [TableCellInputType.Date]: "Date",
  [TableCellInputType.DateTime]: "Date & Time",
  [TableCellInputType.Boolean]: "Boolean",
  [TableCellInputType.Select]: "Select",
  [TableCellInputType.MultiLine]: "Multi-line Text",
  [TableCellInputType.FileUpload]: "File Upload",
  [TableCellInputType.UserInput]: "User Input",
  [TableCellInputType.Form]: "Form",
  [TableCellInputType.SystemProvided]: "System Provided",
};

/**
 * Get display label for a field type
 */
export function getFieldTypeLabel(inputType?: TableCellInputType): string {
  return inputType ? FIELD_TYPE_LABELS[inputType] || "Unknown" : "Unknown";
}
